import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { useGetCurrentUserQuery, useUpdateCurrentUserMutation } from "../../../app/accounts/authuser";

import Input from "../../../components/form-item/Input";
import Calendar from "../../../components/calendar/Calendar";
import Tab from "../../../components/tab/Tab";
import TitleBlock from "../../../components/block/TitleBlock";
import Toggle from "../../../components/form-item/Toggle";
import SvgLoadingAnimation from "../../../components/loading/index";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";

// styled-components
const StyledLink = styled.p`
  width: 100%;
  text-align: center;
  cursor: pointer;
  color: var(--soremo-blue);

  &:hover {
    color: var(--soremo-deep-blue);
  }
`;

const PersonalInformation = () => {
  // RTK Queryフックを使用
  const { data: userData, isLoading, error } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();

  // ローカル状態管理
  const [formData, setFormData] = useState({
    full_name: "",
    date_of_birth: "",
    phone_number: "",
    invoice_registration_number: "",
    billing_postal_code: "",
    billing_prefecture: "",
    billing_address_line1: "",
    billing_address_line2: "",
    is_shipping_same_as_billing: true,
    shipping_postal_code: "",
    shipping_prefecture: "",
    shipping_address_line1: "",
    shipping_address_line2: "",
  });

  const [activeTab, setActiveTab] = useState("請求先"); // タブ管理
  const [errors, setErrors] = useState({}); // エラー管理
  const [updateStatus, setUpdateStatus] = useState("idle"); // 更新状態
  const debounceTimeoutRef = React.useRef(null);
  const isComposingRef = React.useRef(false); // refで管理

  // ユーザーデータの初期化
  useEffect(() => {
    if (userData?.data) {
      setFormData({
        full_name: userData.data.full_name || "",
        date_of_birth: userData.data.date_of_birth || "",
        phone_number: userData.data.phone_number || "",
        invoice_registration_number: userData.data.invoice_registration_number || "",
        billing_postal_code: userData.data.billing_postal_code || "",
        billing_prefecture: userData.data.billing_prefecture || "",
        billing_address_line1: userData.data.billing_address_line1 || "",
        billing_address_line2: userData.data.billing_address_line2 || "",
        is_shipping_same_as_billing: userData.data.is_shipping_same_as_billing ?? true,
        shipping_postal_code: userData.data.shipping_postal_code || "",
        shipping_prefecture: userData.data.shipping_prefecture || "",
        shipping_address_line1: userData.data.shipping_address_line1 || "",
        shipping_address_line2: userData.data.shipping_address_line2 || "",
      });
    }
  }, [userData]);

  // デバウンス付きリアルタイム更新機能
  const debouncedUpdate = useCallback(async (fieldName, fieldValue, autoAddress = false) => {
    try {
      setUpdateStatus("saving");
      await updateCurrentUser({ [fieldName]: fieldValue }).unwrap();

      // 住所自動取得処理
      if (autoAddress) {
        const postalCode = fieldValue.replace("-", "");
        if (postalCode.length === 7) {
          try {
            const response = await axios.get(
              `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${postalCode}`,
            );
            if (response.data.results) {
              const addressData = response.data.results[0];
              const addressFields =
                fieldName === "billing_postal_code"
                  ? {
                      billing_prefecture: addressData.address1,
                      billing_address_line1: `${addressData.address2}${addressData.address3}`,
                    }
                  : {
                      shipping_prefecture: addressData.address1,
                      shipping_address_line1: `${addressData.address2}${addressData.address3}`,
                    };

              setFormData((prev) => ({ ...prev, ...addressFields }));
              // データベースへの反映
              await updateCurrentUser(addressFields).unwrap();
            } else {
              throw new Error("該当する住所が見つかりませんでした。");
            }
          } catch (addressError) {
            console.error("住所取得エラー:", addressError);
          }
        }
      }

      setUpdateStatus("saved");
      // 1秒後にsavedステータスをクリア
      setTimeout(() => setUpdateStatus("idle"), 1000);
    } catch (error) {
      console.error("Failed to update personal information:", error);
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    }
  }, [updateCurrentUser]);

  // 単一フィールドのバリデーション
  const validateField = (name, value) => {
    switch (name) {
      case "full_name":
        if (!value) return "氏名は必須項目です。";
        break;
      case "phone_number":
        if (
          value &&
          !/^(0[5-9]0[-(]?[0-9]{4}[-)]?[0-9]{4}|0120[-]?\d{1,3}[-]?\d{4}|050[-]?\d{4}[-]?\d{4}|0[1-9][-]?\d{1,4}[-]?\d{1,4}[-]?\d{4})*$/.test(
            value,
          )
        ) {
          return "正しい形式で入力してください。";
        }
        break;
      case "invoice_registration_number":
        if (value && !/^\d{13}$/.test(value)) {
          return "13桁の数字を入力してください。";
        }
        break;
      case "billing_postal_code":
      case "shipping_postal_code":
        if (value && !/^[0-9]{3}-?[0-9]{4}$/.test(value)) {
          return "7桁の数字で入力してください。";
        }
        break;
      default:
        break;
    }
    return null;
  };

  // 入力変更時の処理
  const handleChange = (eOrName, valueArg) => {
    let name, value;
    if (typeof eOrName === "string") {
      // compositionendから直接呼ばれた場合
      name = eOrName;
      value = valueArg;
    } else {
      // 通常のonChangeイベント
      name = eOrName.target.name;
      value = eOrName.target.value;
    }

    if (isComposingRef.current) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }

    // ここから下は既存のhandleChangeの処理
    let newValue = value;

    if (name === "date_of_birth") {
      const d = value instanceof Date ? value : new Date(newValue);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      newValue = `${y}-${m}-${day}`;
    }

    if (name === "billing_postal_code" || name === "shipping_postal_code") {
      newValue = newValue.replace(/\D/g, "");
      if (newValue.length > 7) newValue = newValue.slice(0, 7);
      if (newValue.length > 3) {
        newValue = `${newValue.slice(0, 3)}-${newValue.slice(3)}`;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // 単一フィールドのバリデーション実行
    const fieldError = validateField(name, newValue);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError
    }));

    // 既存のタイマーをクリア
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // 500ms後に更新実行
    debounceTimeoutRef.current = setTimeout(() => {
      // バリデーションエラーがない場合のみ更新
      if (!fieldError) {
        debouncedUpdate(
          name,
          newValue,
          name === "billing_postal_code" || name === "shipping_postal_code",
        );
      }
    }, 500);
  };

  // Toggleの変更処理
  const handleToggleChange = () => {
    const newValue = !formData.is_shipping_same_as_billing;
    setFormData((prev) => ({
      ...prev,
      is_shipping_same_as_billing: newValue,
    }));

    // 既存のタイマーをクリア
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // 500ms後に更新実行
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedUpdate("is_shipping_same_as_billing", newValue);
    }, 500);
  };

  // クリーンアップ処理
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // compositionイベントハンドラ
  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };
  const handleCompositionEnd = (e) => {
    // 即座にisComposingをfalseにする
    isComposingRef.current = false;
    // 手動でhandleChangeを呼び出す（onChangeが発火しない場合の対策）
    handleChange(e.target.name, e.target.value);
  };

  if (isLoading) return <SvgLoadingAnimation />;
  if (error) return <div>Error: {error.message || 'データの取得に失敗しました'}</div>;

  return (
    <>
      <TitleBlock title="個人情報" />
      <Link
        to="/privacypolicy"
        target="_blank"
        rel="noopener noreferrer"
        className="u-w100 u-mb64"
      >
        <StyledLink>個人情報の管理方法について確認...</StyledLink>
      </Link>

      <div className="u-col u-gap32 u-w100">
        <div className="u-col u-gap16 u-w100">
          <Input
            type="text"
            label="氏名"
            option="required"
            placeholder="山田 太郎"
            name="full_name"
            error={errors.full_name}
            value={formData.full_name}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
          />
          <Calendar
            label="生年月日"
            option="optional"
            name="date_of_birth"
            value={formData.date_of_birth ? new Date(formData.date_of_birth + "T00:00:00") : ""}
            onChange={handleChange}
            dateFormat="Y年n月j日（D）"
            maxDate={new Date()}
            yearRange={[1920, new Date().getFullYear()]}
          />
          <Input
            type="tel"
            label="電話番号"
            option="optional"
            name="phone_number"
            error={errors.phone_number}
            value={formData.phone_number}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
          />
          <Input
            type="text"
            label="適格請求書発行事業者登録番号"
            option="optional"
            name="invoice_registration_number"
            prepend="T"
            error={errors.invoice_registration_number}
            value={formData.invoice_registration_number}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
          />
        </div>
        <div className="u-w100">
          <Tab
            type="tab"
            list={["請求先", "配送先"]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="u-tab-no-gap"
          />
          <div className="u-col u-gap16 u-w100 u-tab-group">
            {activeTab === "請求先" ? (
              <>
                <div className="u-row u-gap16 u-w100">
                  <Input
                    type="text"
                    label="郵便番号"
                    option="optional"
                    name="billing_postal_code"
                    error={errors.billing_postal_code}
                    value={formData.billing_postal_code}
                    onChange={handleChange}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                  />
                  <Input
                    type="text"
                    label="都道府県"
                    option="optional"
                    name="billing_prefecture"
                    error={errors.billing_prefecture}
                    value={formData.billing_prefecture}
                    onChange={handleChange}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                  />
                </div>
                <Input
                  type="text"
                  label="市区町村"
                  option="optional"
                  name="billing_address_line1"
                  error={errors.billing_address_line1}
                  value={formData.billing_address_line1}
                  onChange={handleChange}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                />
                <Input
                  type="text"
                  label="建物名・部屋番号"
                  option="optional"
                  name="billing_address_line2"
                  error={errors.billing_address_line2}
                  value={formData.billing_address_line2}
                  onChange={handleChange}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                />
              </>
            ) : (
              <>
                <Toggle
                  value={formData.is_shipping_same_as_billing}
                  onClick={handleToggleChange}
                >
                  請求先と同じ
                </Toggle>
                {!formData.is_shipping_same_as_billing && (
                  <>
                    <div className="u-row u-gap16 u-w100">
                     <Input
                       type="text"
                       label="郵便番号"
                       option="optional"
                       name="shipping_postal_code"
                       error={errors.shipping_postal_code}
                       value={formData.shipping_postal_code}
                       onChange={handleChange}
                       onCompositionStart={handleCompositionStart}
                       onCompositionEnd={handleCompositionEnd}
                     />
                     <Input
                       type="text"
                       label="都道府県"
                       option="optional"
                       name="shipping_prefecture"
                       error={errors.shipping_prefecture}
                       value={formData.shipping_prefecture}
                       onChange={handleChange}
                       onCompositionStart={handleCompositionStart}
                       onCompositionEnd={handleCompositionEnd}
                     />
                     </div>

                     <Input
                       type="text"
                       label="市区町村"
                       option="optional"
                       name="shipping_address_line1"
                       error={errors.shipping_address_line1}
                       value={formData.shipping_address_line1}
                       onChange={handleChange}
                       onCompositionStart={handleCompositionStart}
                       onCompositionEnd={handleCompositionEnd}
                     />
                     <Input
                       type="text"
                       label="建物名・部屋番号"
                       option="optional"
                       name="shipping_address_line2"
                       error={errors.shipping_address_line2}
                       value={formData.shipping_address_line2}
                       onChange={handleChange}
                       onCompositionStart={handleCompositionStart}
                       onCompositionEnd={handleCompositionEnd}
                     />
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <StatusIndicator status={updateStatus} />
      </div>
    </>
  );
};

export default PersonalInformation;
